import { action, makeAutoObservable, observable, runInAction } from 'mobx';

import { GQL } from '../../../../../../../gql/client';
import {
  CreateCardInput,
  CreateUserInput,
  CreateWaitingListItemMutation,
  CreateWaitingListItemMutationVariables,
  Finish3dSecureCardInfo,
  RegisterUserMutation,
  RegisterUserMutationVariables,
} from '../../../../../../../gql/gql-types';
import { createWaitingListItem } from '../../../../../../../gql/operations/createWaitingListItem';
import { registerUser } from '../../../../../../../gql/operations/registerUser';
import { Routes } from '../../../../../../constants/Routes';
import { Card } from '../../../../../../domains/customer/Card';
import { CreditCardStore } from '../../../../store/CreditCardStore';
import { ErrorInfo, Name } from '../../../stores/ProfileStore';

export class GuestWaitingListStore {
  constructor() {
    makeAutoObservable(this, {
      name: observable,
      setName: action,
      valid: observable,
      fix: action,
      userId: observable,
      errors: observable,
      mail: observable,
      setMail: action,
      card: observable,
      setCard: action,
      phone: observable,
      setPhone: action,
    });
  }

  public name?: Name = undefined;
  public mail?: string = undefined;
  public userId?: string = undefined;
  public phone?: string = undefined;
  public valid = false;
  public errors: ErrorInfo[] = [];

  public get fixable() {
    return this.valid;
  }

  public clinic?: string = undefined;
  public department?: string = undefined;
  public treatmentKind?: string = undefined;

  public start: string = new Date().toISOString();
  public end: string = new Date().toISOString();

  public newComer = true;

  public setNewComer(newComer: boolean) {
    this.newComer = newComer;
  }

  public async fix(): Promise<string | undefined> {
    if (!this.userId) {
      return;
    }

    if (this.newComer) {
      const user: CreateUserInput = {
        id: this.userId,
        // eslint-disable-next-line no-irregular-whitespace
        name: this.name ? `${this.name.sei}　${this.name.mei}` : '',
        mail: this.mail,
        sei: this.name?.sei,
        mei: this.name?.mei,
        confirmed: [],
        insuranceCards: [],
        phone: this.phone || '',
      };

      const card: CreateCardInput | undefined = this.card
        ? {
            id: '_',
            cardCustomerId: '_',
            fourDigit: this.card.fourDigit || '',
            brand: this.card.brand || '',
            payjpID: this.card.id || '',
            expAt: this.card.expAt || '',
          }
        : undefined;

      await GQL.query<RegisterUserMutationVariables, RegisterUserMutation>(registerUser, {
        input: {
          user,
          card,
        },
      });
    }

    await GQL.query<CreateWaitingListItemMutationVariables, CreateWaitingListItemMutation>(createWaitingListItem, {
      input: {
        userId: this.userId,
        clinic: this.clinic,
        department: this.department,
        treatmentKind: this.treatmentKind,
        start: this.start,
        end: this.end,
      },
    });
    return this.id;
  }

  public setName(name: Name) {
    this.name = name;
    this.checkNameValidity();
  }

  public setMail(mail: string) {
    this.mail = mail;
  }

  private checkNameValidity() {
    runInAction(() => {
      // 名前関連のエラーをクリア
      this.errors = this.errors.filter(error => error.field !== 'sei' && error.field !== 'mei');

      if (!this.name) {
        this.errors = [...this.errors, { field: 'sei', error: '氏名を入力してください。' }];
        this.valid = false;
        return;
      }
      if (!/^([ァ-ン]|ー)+$/.test(this.name.sei)) {
        this.errors = [...this.errors, { field: 'sei', error: '氏名は全角カタカナで入力してください。' }];
      }
      if (!/^([ァ-ン]|ー)+$/.test(this.name.mei)) {
        this.errors = [...this.errors, { field: 'mei', error: '氏名は全角カタカナで入力してください。' }];
      }

      // エラーが残っていればvalidをfalseに設定
      this.valid = this.errors.length === 0;
    });
  }

  private checkPhoneValidity() {
    runInAction(() => {
      // 電話番号関連のエラーをクリア
      this.errors = this.errors.filter(error => error.field !== 'phone');

      if (!this.phone) {
        this.errors = [...this.errors, { field: 'phone', error: '電話番号を入力してください。' }];
        this.valid = false;
        return;
      }

      if (this.phone.startsWith('+81')) {
        const localNumber = this.phone.substring(3);
        if (!/^\d{9,10}$/.test(localNumber)) {
          this.errors = [...this.errors, { field: 'phone', error: '10桁または11桁で入力してください。' }];
        }
      } else {
        if (!/^\+\d{2,}\d{7,11}$/.test(this.phone)) {
          this.errors = [...this.errors, { field: 'phone', error: '電話番号の桁数を確認してください。' }];
        }
      }

      // エラーが残っていればvalidをfalseに設定
      this.valid = this.errors.length === 0;
    });
  }

  public get id() {
    return `${this.clinic}#${this.start}#${this.department}#${this.treatmentKind || this.department}#${this.userId}`;
  }

  public card?: Card = undefined;

  public setCard(card: Card) {
    this.card = card;
  }

  public cardStatus: CreditCardStore = new CreditCardStore();

  public setPhone(phone: string) {
    this.phone = phone;
    this.checkPhoneValidity();
  }

  /**
   * 3Dセキュア認証前に入力内容を一時保存する
   */
  public saveToSession() {
    const data = {
      name: this.name,
      mail: this.mail,
      userId: this.userId,
      phone: this.phone,
      clinic: this.clinic,
      department: this.department,
      treatmentKind: this.treatmentKind,
      start: this.start,
      end: this.end,
      newComer: this.newComer,
    };

    sessionStorage.setItem('tempWaitingList', JSON.stringify(data));
  }

  /**
   * 一時保存した入力内容を復元する
   */
  public loadFromSession() {
    const json = sessionStorage.getItem('tempWaitingList');
    if (!json) return;

    try {
      const data = JSON.parse(json) as {
        name?: Name;
        mail?: string;
        userId?: string;
        phone?: string;
        clinic?: string;
        department?: string;
        treatmentKind?: string;
        start?: string;
        end?: string;
        newComer?: boolean;
      };
      this.userId = data.userId;
      this.clinic = data.clinic;
      this.department = data.department;
      this.treatmentKind = data.treatmentKind;
      this.start = data.start || this.start;
      this.end = data.end || this.end;
      this.newComer = data.newComer ?? this.newComer;

      if (data.name) {
        this.setName(data.name);
      } else {
        this.name = undefined;
      }

      if (data.phone) {
        this.setPhone(data.phone);
      } else {
        this.phone = undefined;
      }

      if (data.mail) {
        this.setMail(data.mail);
      } else {
        this.mail = undefined;
      }

      this.errors = [];
      this.checkNameValidity();
      this.checkPhoneValidity();
    } catch (e) {
      console.error('Failed to load waiting list data from session:', e);
    }
  }

  /**
   * 一時保存した入力内容を削除する
   */
  public clearSession() {
    sessionStorage.removeItem('tempWaitingList');
  }

  /**
   * 3Dセキュア完了後の処理
   * 他のstoreにも同様のメソッドを実装しているが、各storeでメソッド名やフローが異なるため処理内容の統一はしていない
   */
  public async completeAfter3DSecure(params: {
    waitingIdParam: string | null;
    decode: (v: string) => string;
    cardInfo?: Finish3dSecureCardInfo | null;
  }): Promise<{
    redirectPath: string;
    fixedId?: string;
    toastMessage: string;
  }> {
    try {
      if (params.cardInfo) {
        this.setCard({
          id: params.cardInfo.payjpID,
          fourDigit: params.cardInfo.fourDigit,
          brand: params.cardInfo.brand,
          expAt: params.cardInfo.expAt,
        });
      }
      // 1. ユーザーが途中まで入力してた内容を復元
      this.loadFromSession();

      // 2. URLにもらってきたwaitingIdがないと確定できないのでバリデーション
      if (!params.waitingIdParam) {
        throw new Error('キャンセル待ちIDが指定されていません');
      }

      // 3. fix()でバックエンドに確定登録させる
      const fixedId = await this.fix(); // 新規登録＋カード＋waitingListItemの作成まで面倒みる設計

      // 4. URL由来のwaitingIdもデコード
      const waitingId = params.decode(params.waitingIdParam);

      // 5. pushに渡すべきIDを決定
      const idToUse = fixedId ?? waitingId;
      if (!idToUse) {
        throw new Error('キャンセル待ちの確定に失敗しました');
      }

      // 6. 画面に返す情報
      return {
        redirectPath: Routes.Paths.WaitingSuccess,
        fixedId: idToUse,
        toastMessage: 'キャンセル待ちを確定しました。',
      };
    } finally {
      // 成功でも失敗でもセッション上の一時データは掃除する
      this.clearSession();
    }
  }
}
